import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Mail, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const t = {
    tr: {
      forgotPassword: 'Şifremi Unuttum',
      resetPassword: 'Şifre Sıfırla',
      enterEmail: 'Email adresinizi girin',
      emailPlaceholder: 'ornek@email.com',
      sendResetLink: 'Sıfırlama Linki Gönder',
      backToLogin: 'Giriş\'e Dön',
      emailSentTitle: 'Email Gönderildi!',
      emailSentDesc: 'Şifre sıfırlama linki email adresinize gönderildi. Lütfen gelen kutunuzu kontrol edin.',
      newPassword: 'Yeni Şifre',
      confirmPassword: 'Şifreyi Onayla',
      resetButton: 'Şifreyi Sıfırla',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordTooShort: 'Şifre en az 8 karakter olmalı',
      resetSuccessTitle: 'Şifre Sıfırlandı!',
      resetSuccessDesc: 'Şifreniz başarıyla değiştirildi. Artık yeni şifrenizle giriş yapabilirsiniz.',
      goToLogin: 'Giriş Yap',
      invalidToken: 'Geçersiz veya süresi dolmuş link',
      error: 'Bir hata oluştu'
    },
    en: {
      forgotPassword: 'Forgot Password',
      resetPassword: 'Reset Password',
      enterEmail: 'Enter your email address',
      emailPlaceholder: 'example@email.com',
      sendResetLink: 'Send Reset Link',
      backToLogin: 'Back to Login',
      emailSentTitle: 'Email Sent!',
      emailSentDesc: 'A password reset link has been sent to your email. Please check your inbox.',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      resetButton: 'Reset Password',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      resetSuccessTitle: 'Password Reset!',
      resetSuccessDesc: 'Your password has been successfully changed. You can now login with your new password.',
      goToLogin: 'Go to Login',
      invalidToken: 'Invalid or expired link',
      error: 'An error occurred'
    }
  };
  
  const text = t[language] || t.tr;
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setEmailSent(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || text.error);
      }
    } catch (error) {
      toast.error(text.error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error(text.passwordTooShort);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(text.passwordMismatch);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      if (response.ok) {
        setResetSuccess(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || text.error);
      }
    } catch (error) {
      toast.error(text.error);
    } finally {
      setLoading(false);
    }
  };
  
  // Reset password form (when token is present)
  if (token) {
    if (resetSuccess) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">{text.resetSuccessTitle}</CardTitle>
              <CardDescription>{text.resetSuccessDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="w-full" 
                onClick={() => navigate('/login')}
              >
                {text.goToLogin}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Lock className="w-6 h-6" />
              {text.resetPassword}
            </CardTitle>
            <CardDescription>
              {language === 'tr' ? 'Yeni şifrenizi belirleyin' : 'Set your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{text.newPassword}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{text.confirmPassword}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? '...' : text.resetButton}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Email sent success
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">{text.emailSentTitle}</CardTitle>
            <CardDescription>{text.emailSentDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {text.backToLogin}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Forgot password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Lock className="w-6 h-6" />
            {text.forgotPassword}
          </CardTitle>
          <CardDescription>{text.enterEmail}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={text.emailPlaceholder}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? '...' : text.sendResetLink}
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full mt-2">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {text.backToLogin}
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
